// ============================================================================
// BID SERVICE
// All API calls for the Bid Response module: document upload/list/delete
// (story #174) and requirement extraction/review (story #175).
// ============================================================================

import { api } from './api';

export type BidDocumentClass = 'tender' | 'evidence';

export interface BidEvidenceMetadata {
  client?: string;
  date?: string;
  quantifiedOutcomes?: string;
  sourcedStatus: 'sourced' | 'unsourced';
}

export interface BidDocument {
  documentId: string;
  projectId: string;
  docClass: BidDocumentClass;
  fileName: string;
  fileType: string;
  fileSizeBytes: number;
  s3Key: string;
  uploadedBy: string;
  uploadedAt: string;
  evidence?: BidEvidenceMetadata;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  processingError?: string;
  supersedesDocumentId?: string;
}

export interface BidUploadUrlResponse {
  uploadUrl: string;
  documentId: string;
  s3Key: string;
}

export type BidRequirementStatus = 'Covered' | 'Partial' | 'Gap' | 'N/A';
export type BidRequirementReviewStatus = 'pending_review' | 'reviewed';

export interface BidRequirementSourceRef {
  documentId: string;
  page?: number;
  section?: string;
  sheetName?: string;
  rowIndex?: number;
}

export interface BidRequirement {
  requirementId: string;
  projectId: string;
  documentId: string;
  sourceRef: BidRequirementSourceRef;
  clauseRef?: string;
  text: string;
  category?: string;
  weight?: number;
  deadline?: string;
  status: BidRequirementStatus;
  reviewStatus: BidRequirementReviewStatus;
  openIssues: string[];
  extractionMethod: 'llm' | 'xlsx-row-map';
  createdAt: string;
  supersedesRequirementId?: string;
  supersededByRequirementId?: string;
  changeStatus?: 'new' | 'modified' | 'unchanged' | 'removed';
  wordLimit?: number;
  wordCount?: number;
  potRelativeWeight?: number;
  totalBidRelativeWeight?: number;
  linkedWinThemeId?: string;
  linkedEvidenceIds?: string[];
  parentRequirementId?: string;
}

export interface BidDiffResult {
  predecessorDocumentId: string;
  new: BidRequirement[];
  modified: BidRequirement[];
  unchanged: BidRequirement[];
  removed: BidRequirement[];
}

// Session extraction hook — story #177
export type BidExtractionStatus = 'generating' | 'pending-review' | 'confirmed' | 'dismissed';

export interface BidExtractionDraft {
  requirements: BidRequirement[];
  isEmpty: boolean;
}

export interface BidExtraction {
  sessionId: string;
  sessionType: string;
  status: BidExtractionStatus;
  requestId: string;
  generatedAt?: string;
  confirmedAt?: string;
  confirmedBy?: string;
  draft?: BidExtractionDraft;
  confirmed?: BidExtractionDraft;
  highWaterMark?: string;
}

// Typed evidence pulled from any standard co-creation session — story #177
export type BidEvidenceType = 'persona' | 'userStory' | 'nfr' | 'adr' | 'winTheme';

export interface BidEvidence {
  evidenceType: BidEvidenceType;
  sourceId: string;
  sourceSessionId?: string;
  projectId: string;
  title: string;
  summary: string;
  raw: unknown;
  pulledAt: string;
}

export interface BidEvidencePullResult {
  counts: Record<BidEvidenceType, number>;
}

export const bidDocumentsAPI = {
  list: (projectId: string, docClass?: BidDocumentClass) =>
    api.get<{ documents: BidDocument[] }>(`/api/projects/${projectId}/bid/documents`, {
      params: docClass ? { docClass } : undefined
    }),

  get: (projectId: string, documentId: string) =>
    api.get<{ document: BidDocument }>(`/api/projects/${projectId}/bid/documents/${documentId}`),

  generateUploadUrl: (
    projectId: string,
    fileName: string,
    fileType: string,
    fileSize: number,
    docClass: BidDocumentClass,
    evidence?: BidEvidenceMetadata,
    supersedesDocumentId?: string
  ) =>
    api.post<BidUploadUrlResponse>(`/api/projects/${projectId}/bid/documents/upload`, {
      fileName, fileType, fileSize, docClass, evidence, supersedesDocumentId
    }),

  delete: (projectId: string, documentId: string) =>
    api.delete(`/api/projects/${projectId}/bid/documents/${documentId}`),

  triggerExtraction: (projectId: string, documentId: string) =>
    api.post<{ success: boolean }>(`/api/projects/${projectId}/bid/documents/${documentId}/extract`),

  setSupersedesDocumentId: (projectId: string, documentId: string, supersedesDocumentId: string | null) =>
    api.patch<{ document: BidDocument }>(`/api/projects/${projectId}/bid/documents/${documentId}`, { supersedesDocumentId }),

  runDiff: (projectId: string, documentId: string) =>
    api.post<BidDiffResult>(`/api/projects/${projectId}/bid/documents/${documentId}/diff`),

  getDiff: (projectId: string, documentId: string) =>
    api.get<BidDiffResult>(`/api/projects/${projectId}/bid/documents/${documentId}/diff`)
};

export const bidRequirementsAPI = {
  list: (projectId: string, documentId?: string) =>
    api.get<{ requirements: BidRequirement[] }>(`/api/projects/${projectId}/bid/requirements`, {
      params: documentId ? { documentId } : undefined
    }),

  get: (projectId: string, requirementId: string) =>
    api.get<{ requirement: BidRequirement }>(`/api/projects/${projectId}/bid/requirements/${requirementId}`),

  review: (projectId: string, requirementId: string, updates: { reviewStatus?: BidRequirementReviewStatus; openIssues?: string[] }) =>
    api.patch<{ requirement: BidRequirement }>(`/api/projects/${projectId}/bid/requirements/${requirementId}/review`, updates)
};

export const bidExtractionsAPI = {
  get: (projectId: string, sessionId: string) =>
    api.get<{ extraction: BidExtraction }>(`/api/projects/${projectId}/bid/extractions/${sessionId}`),

  trigger: (projectId: string, sessionId: string, sessionType: string) =>
    api.post<{ success: boolean; requestId: string; status: string }>(
      `/api/projects/${projectId}/bid/extractions/${sessionId}`,
      { sessionType }
    ),

  confirm: (projectId: string, sessionId: string) =>
    api.post<{ success: boolean }>(`/api/projects/${projectId}/bid/extractions/${sessionId}/confirm`)
};

export const bidEvidenceAPI = {
  list: (projectId: string, evidenceType?: BidEvidenceType) =>
    api.get<{ evidence: BidEvidence[] }>(`/api/projects/${projectId}/bid/evidence`, {
      params: evidenceType ? { type: evidenceType } : undefined
    }),

  pull: (projectId: string, sessionId: string) =>
    api.post<BidEvidencePullResult>(`/api/projects/${projectId}/bid/evidence/pull`, { sessionId })
};
